// @figma file=tbd node=tbd name="ChatInput" set="code-factory" verified=tbd

import { ArrowUp } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../atoms/button';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChatInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  /** Controlled value */
  value?: string;
  onValueChange?: (value: string) => void;
  /** Called when the user submits (Enter or send button) */
  onSubmit?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Slot rendered to the left of the textarea (attachment/options buttons) */
  leading?: React.ReactNode;
  /** Slot rendered to the right of the send button */
  trailing?: React.ReactNode;
  /** Max height before the textarea starts scrolling */
  maxRows?: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ChatInput = React.forwardRef<HTMLDivElement, ChatInputProps>(
  (
    {
      value: valueProp,
      onValueChange,
      onSubmit,
      placeholder = 'Ask anything',
      disabled = false,
      leading,
      trailing,
      maxRows = 8,
      className,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = React.useState('');
    const isControlled = valueProp !== undefined;
    const value = isControlled ? valueProp : internalValue;

    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea
    const resize = React.useCallback(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      const lineHeight = parseInt(getComputedStyle(el).lineHeight, 10) || 20;
      const maxHeight = lineHeight * maxRows;
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    }, [maxRows]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value;
      if (!isControlled) setInternalValue(next);
      onValueChange?.(next);
      resize();
    };

    const handleSubmit = React.useCallback(() => {
      const trimmed = value.trim();
      if (!trimmed || disabled) return;
      onSubmit?.(trimmed);
      if (!isControlled) setInternalValue('');
      // Reset height
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }, [value, disabled, isControlled, onSubmit]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    const canSubmit = value.trim().length > 0 && !disabled;

    return (
      <div
        ref={ref}
        className={cn(
          'w-full rounded-2xl border border-border bg-background shadow-sm transition-shadow focus-within:shadow-md',
          className,
        )}
        {...props}
      >
        {/* Textarea */}
        <div className="px-3 pt-3 pb-1">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            aria-label={placeholder}
            className={cn(
              'w-full resize-none bg-transparent text-base sm:text-sm text-foreground placeholder:text-muted-foreground',
              'border-0 p-0 outline-none focus:ring-0',
              'min-h-[1.5rem] overflow-y-auto',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          />
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-1 px-2 pb-2">
          {/* Leading slot */}
          {leading && <div className="flex items-center gap-1">{leading}</div>}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Trailing slot + send */}
          {trailing && <div className="flex items-center gap-1">{trailing}</div>}

          <Button
            type="button"
            size="icon"
            className={cn(
              'size-8 rounded-full transition-opacity',
              canSubmit ? 'opacity-100' : 'opacity-40 pointer-events-none',
            )}
            aria-label="Send message"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            <ArrowUp className="size-4" />
          </Button>
        </div>
      </div>
    );
  },
);
ChatInput.displayName = 'ChatInput';

export { ChatInput };
